<template>
  <div v-if="isOpen" class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
    <div
      class="flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
      :class="isViewerOpen ? 'h-screen' : 'max-h-screen overflow-y-auto p-5'"
      style="max-height: 92vh"
    >
      <template v-if="isViewerOpen">
        <DocumentViewer
          :title="currentDocument.title"
          :documentUrl="currentDocument.url"
          :checkboxLabel="currentDocument.checkboxLabel"
          :idPrefix="currentDocument.key"
          :checked="acknowledgements[currentDocument.acknowledgementKey]"
          @update:checked="updateAcknowledgement(currentDocument.acknowledgementKey, $event)"
          @close="closeDocumentViewer"
        />
      </template>

      <template v-else>
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900">Consultation des documents contractuels et signature</h3>
          <button type="button" class="text-gray-500 hover:text-gray-700" @click="$emit('close')">Fermer</button>
        </div>

        <div class="mb-4 space-y-3">
          <p class="text-sm text-gray-600 mb-4">Veuillez consulter les documents suivants avant de continuer :</p>
          <LinkField
            v-for="doc in documentConfigs"
            :key="doc.key"
            variant="card"
            as="button"
            icon="document"
            :label="doc.title"
            :description="
              acknowledgements[doc.acknowledgementKey]
                ? 'Document consulté — cliquez pour le revoir'
                : 'PDF à consulter avant de continuer'
            "
            :status="acknowledgements[doc.acknowledgementKey] ? 'done' : 'pending'"
            :statusLabel="acknowledgements[doc.acknowledgementKey] ? 'Consulté' : 'À consulter'"
            @click="openDocumentViewer(doc.key)"
          />
        </div>

        <div class="space-y-4 text-sm text-gray-800">
          <Checkbox
            id="insurance_consent"
            name="insurance_consent"
            :modelValue="acknowledgements.insuranceConsent"
            :label="insuranceConsentText"
            labelClass="font-normal text-gray-800"
            @update:modelValue="updateAcknowledgement('insuranceConsent', $event)"
          />

          <div class="pl-6">
            <p>J'atteste à ce titre :</p>
            <ul class="list-disc pl-5 mt-1 space-y-1">
              <li>
                L'exactitude des renseignements communiqués et être informé que toute réticence ou déclaration inexacte
                sont soumises selon les cas aux sanctions prévues aux articles L.113-8 et L.113-9 du Code des assurances
                ;
              </li>
              <li>
                Que l'appareil garanti a été acheté neuf ou reconditionné il y a moins de 30 jours auprès d'un point de
                vente du distributeur habilité de Estaly proposant l'assurance ;
              </li>
              <li>
                Que mes besoins et exigences en termes de niveaux de garantie et de couverture ont bien été recueillis
                en point de vente, que les différentes formules d'assurance m'ont été présentées préalablement à
                l'adhésion et que j'ai été à ce titre suffisamment informé(e) et conseillé(e) ;
              </li>
              <li v-if="isMobileProtection">
                Avoir reçu et pris connaissance préalablement à l'adhésion du Document d'Information sur le Produit
                d'Assurance, de la Fiche d'Information et de Conseil préalable à l'adhésion, ainsi que de la notice
                d'information valant dispositions générales réf. AEI30004-03, disponibles en point de vente et m'engager
                à les conserver.
              </li>
            </ul>
          </div>

          <Checkbox
            id="digital_delivery_consent"
            name="digital_delivery_consent"
            :modelValue="acknowledgements.digitalDeliveryConsent"
            :label="digitalDeliveryConsentText"
            labelClass="font-normal text-gray-800"
            @update:modelValue="updateAcknowledgement('digitalDeliveryConsent', $event)"
          />
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg px-4 py-2 text-sm text-white"
            :class="canContinue ? 'bg-primary hover:bg-primary-dark' : 'bg-gray-300 cursor-not-allowed'"
            :disabled="!canContinue"
            @click="$emit('continue')"
          >
            Continuer
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { Checkbox, LinkField, isConnectProtection, isMobileProtection } from "@estaly/ui";
import DocumentViewer from "@/components/pos/subscription/formwithsteps/DocumentViewer.vue";

export default {
  name: "ContractConsultationModal",
  components: {
    Checkbox,
    LinkField,
    DocumentViewer,
  },
  emits: ["close", "continue", "update:acknowledgements"],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    offerCategory: {
      type: String,
      default: "",
    },
    acknowledgements: {
      type: Object,
      required: true,
    },
    advisoryAgreementUrl: {
      type: String,
      default: "",
    },
    informationNoticeLinkUrl: {
      type: String,
      default: "",
    },
    productInformationSheetLinkUrl: {
      type: String,
      default: "",
    },
    canContinue: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeDocumentKey: null,
    };
  },

  computed: {
    isMobileProtection() {
      return isMobileProtection(this.offerCategory);
    },
    insuranceConsentText() {
      const key = isConnectProtection(this.offerCategory) ? "connect_protection" : "mobile_protection";
      return this.$t(`contractConsultation.insuranceConsent.${key}`);
    },
    digitalDeliveryConsentText() {
      return "J'accepte de recevoir les éléments d'information sur le contrat d'assurance par voie dématérialisée.";
    },
    documentConfigs() {
      return [
        {
          key: "product_information_sheet",
          title: "Document d'information sur le produit d'assurance",
          url: this.productInformationSheetLinkUrl,
          acknowledgementKey: "productInformationSheetViewed",
          checkboxLabel: "Je confirme avoir consulté le document d'information sur le produit d'assurance.",
        },
        {
          key: "advisory_agreement",
          title: "Fiche d'information contractuelle d'assurance",
          url: this.advisoryAgreementUrl,
          acknowledgementKey: "advisoryAgreementViewed",
          checkboxLabel: "Je confirme avoir consulté la fiche d'information contractuelle d'assurance.",
        },
        {
          key: "information_notice",
          title: "Notice d'information valant conditions générales",
          url: this.informationNoticeLinkUrl,
          acknowledgementKey: "informationNoticeViewed",
          checkboxLabel: "Je confirme avoir consulté la notice d'information valant conditions générales.",
        },
      ];
    },
    isViewerOpen() {
      return this.activeDocumentKey !== null;
    },
    currentDocument() {
      return (
        this.documentConfigs.find((doc) => doc.key === this.activeDocumentKey) || {
          key: "",
          title: "",
          url: "",
          acknowledgementKey: "",
          checkboxLabel: "",
        }
      );
    },
  },

  watch: {
    isOpen(open) {
      if (!open) {
        this.activeDocumentKey = null;
      }
    },
  },

  methods: {
    updateAcknowledgement(key, value) {
      this.$emit("update:acknowledgements", {
        ...this.acknowledgements,
        [key]: value,
      });
    },
    openDocumentViewer(documentKey) {
      this.activeDocumentKey = documentKey;
    },
    closeDocumentViewer() {
      this.activeDocumentKey = null;
    },
  },
};
</script>
